import { useState, useMemo, useEffect } from "react";
import {
  Download,
  RotateCcw,
  FileText,
  Clock,
  DollarSign,
  X,
  Handshake,
  Wallet,
  Archive,
  Percent,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Select, Collapse } from "antd";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { downloadBillingSummaryExcel } from "@/lib/exportBillingSummaryExcel";
import {
  formatCurrencyValue,
  formatNumber,
  parseAmount,
} from "@/lib/formatNumber";
import { Label } from "../../components/ui/label";
import { useQuery } from "@tanstack/react-query";
import { get } from "@/services/axiosUitils";
import {
  DropdownService,
  DynamicModuleService,
  PtsProjectService,
} from "@/services/APIs";

const EMPLOYEE_SCHEMA_NAME = "PTS Emp";
const PTS_PROJECT_SCHEMA_NAME = "PTS Project";

// API response types for /pts_app/billing-summary/
interface BillingEmployee {
  employee_id: string;
  employee_name: string;
  role?: string;
  role_hourly_rate?: number;
  billable_hrs: string;
  Actual_cost: number;
  billable_value: number;
  Expenses: number;
  Margin: string;
}

/** API uses spaced keys for expense totals (root and per-project). */
type ExpenseTotalKeys = {
  total_old_cost?: number;
  "total Travelling Expense"?: number;
  "total Server Expense"?: number;
  "total Hosting Expense"?: number;
  "total Expense"?: number;
};

type ProjectExpenseKeys = {
  total_old_cost?: number;
  "Total Travelling Expense"?: number;
  "Total Server Expense"?: number;
  "Total Hosting Expense"?: number;
};

interface BillingProject extends ProjectExpenseKeys {
  project_id: string;
  project_name: string;
  project_type: string;
  currency: string;
  currency_symbol: string;
  start_date?: string | null;
  employees: BillingEmployee[];
}

interface BillingSummaryResponse extends ExpenseTotalKeys {
  data: BillingProject[];
  old_hours: string;
  billable_hrs: string;
  total_billable_hrs: string;
  new_billable_value: number;
  old_billable_value: number;
  total_billable_value: number;
  total_agreement_costing: number;
  actual_salary_cost: number;
  total_cost: number;
  Profit_loss_base_On_billable_value: number;
  Profit_loss_base_On_proposal_budget: number;
  "Project health": number;
}

function padDatePart(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function parseBillableHoursToMinutes(
  value: string | number | null | undefined
): number {
  if (value === null || value === undefined) {
    return 0;
  }

  const raw = String(value).trim();
  if (!raw) {
    return 0;
  }

  if (raw.includes(":")) {
    const [hPart = "0", mPart = "0"] = raw.split(":");
    const h = Math.max(0, Number.parseInt(hPart, 10) || 0);
    const m = Math.min(59, Math.max(0, Number.parseInt(mPart, 10) || 0));
    return h * 60 + m;
  }

  const numericHours = Number(raw);
  if (!Number.isFinite(numericHours)) {
    return 0;
  }

  return Math.max(0, Math.round(numericHours * 60));
}

function isLikelyUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    value.trim()
  );
}

function getCurrencyExportValue(project: BillingProject): string {
  const symbol = (project.currency_symbol ?? "").trim();
  if (symbol) {
    return symbol;
  }
  const currencyValue = (project.currency ?? "").trim();
  if (!currencyValue || isLikelyUuid(currencyValue)) {
    return "";
  }
  return currencyValue;
}

/** Default to current month start and end in local time (avoids UTC shift). */
function getDefaultDates(): { startDate: string; endDate: string } {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth();
  const start = new Date(y, m, 1);
  const end = new Date(y, m + 1, 0);
  const startDate = `${start.getFullYear()}-${padDatePart(start.getMonth() + 1)}-${padDatePart(start.getDate())}`;
  const endDate = `${end.getFullYear()}-${padDatePart(end.getMonth() + 1)}-${padDatePart(end.getDate())}`;
  return { startDate, endDate };
}

const defaultDates = getDefaultDates();

function normalizeApiDate(value?: string | null): string | null {
  if (!value) {
    return null;
  }
  return value.split("T")[0] || null;
}

const BillingSummaryPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState(defaultDates.startDate);
  const [endDate, setEndDate] = useState(defaultDates.endDate);
  const [projectId, setProjectId] = useState<string | undefined>(undefined);
  const [employeeId, setEmployeeId] = useState<string | undefined>(undefined);
  const [taskId, setTaskId] = useState<string | undefined>(undefined);
  const [subTaskId, setSubTaskId] = useState<string | undefined>(undefined);

  const handleProjectChange = (value?: string) => {
    setProjectId(value ?? undefined);
    setTaskId(undefined);
    setSubTaskId(undefined);
  };

  const handleTaskChange = (value?: string) => {
    setTaskId(value ?? undefined);
    setSubTaskId(undefined);
  };

  const { data: projectOptions } = useQuery({
    queryKey: ["open-project-list"],
    queryFn: () => PtsProjectService.getOpenProjectList(),
  });

  const { data: employeeOptions } = useQuery({
    queryKey: ["employee-dropdown", EMPLOYEE_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(EMPLOYEE_SCHEMA_NAME),
    enabled: !!EMPLOYEE_SCHEMA_NAME,
  });

  const { data: taskOptions } = useQuery({
    queryKey: ["billing-summary-project-task", projectId],
    queryFn: async () => {
      if (!projectId) {
        return [];
      }
      const response = await get<any>("/pts_app/project-task/", {
        params: { project: projectId },
      });

      if (
        response?.success &&
        response?.tasks &&
        Array.isArray(response.tasks)
      ) {
        return response.tasks.map((task: any) => ({
          value: String(task.id),
          label: task.taskname ?? task.name ?? String(task.id),
        }));
      }

      if (Array.isArray(response)) {
        return response.map((task: any) => ({
          value: String(task.id ?? task.value),
          label: task.taskname ?? task.name ?? task.label ?? String(task.id),
        }));
      }

      return [];
    },
    enabled: !!projectId,
  });

  const { data: subTaskOptions } = useQuery({
    queryKey: ["billing-summary-task-subtask", taskId],
    queryFn: async () => {
      if (!taskId) {
        return [];
      }
      const response = await get<any>(
        "/pts_app/project-summary/task-subtask/",
        {
          params: { task_id: taskId },
        }
      );
      const listing = response?.subtask_listing;
      if (!Array.isArray(listing)) {
        return [];
      }
      return listing.map((subTask: any) => ({
        value: String(subTask.id),
        label: subTask.taskname ?? String(subTask.id),
      }));
    },
    enabled: !!taskId,
  });

  const { data: billingResponse, isLoading: billingLoading } = useQuery({
    queryKey: [
      "billing-summary",
      projectId,
      employeeId,
      taskId,
      subTaskId,
      startDate,
      endDate,
    ],
    queryFn: async () => {
      const params: Record<string, string> = {
        start_date: startDate,
        end_date: endDate,
      };
      if (projectId) {
        params.project_id = projectId;
      }
      if (employeeId) {
        params.employee_id = employeeId;
      }
      if (taskId) {
        params.task = taskId;
      }
      if (subTaskId) {
        params.sub_task = subTaskId;
      }
      const res = await get<BillingSummaryResponse>(
        "/pts_app/billing-summary/",
        { params }
      );
      return res;
    },
    enabled: !!startDate && !!endDate,
  });

  const { data: selectedProjectRecord } = useQuery({
    queryKey: ["billing-summary-pts-project", projectId],
    queryFn: () =>
      DynamicModuleService.getItemById(PTS_PROJECT_SCHEMA_NAME, projectId!),
    enabled: !!projectId,
  });

  useEffect(() => {
    if (!projectId) {
      setStartDate(prev =>
        prev === defaultDates.startDate ? prev : defaultDates.startDate
      );
      return;
    }
    const record = selectedProjectRecord as Record<string, unknown> | undefined;
    const raw =
      (typeof record?.start_date === "string" && record.start_date) ||
      (typeof record?.startdate === "string" && record.startdate) ||
      (typeof record?.StartDate === "string" && record.StartDate) ||
      null;
    const projectStartDate = normalizeApiDate(raw);
    if (!projectStartDate) {
      return;
    }
    setStartDate(prev => (prev === projectStartDate ? prev : projectStartDate));
  }, [projectId, selectedProjectRecord]);

  const filteredData = useMemo(() => {
    const apiData = billingResponse?.data ?? [];
    if (!searchQuery.trim()) {
      return apiData;
    }
    const q = searchQuery.toLowerCase();
    return apiData
      .map(project => ({
        ...project,
        employees: project.employees.filter(
          emp =>
            emp.employee_name.toLowerCase().includes(q) ||
            project.project_name.toLowerCase().includes(q)
        ),
      }))
      .filter(project => project.employees.length > 0);
  }, [billingResponse?.data, searchQuery]);

  const selectedProjectLabel = useMemo(() => {
    if (!projectId) {
      return "All projects";
    }
    const match = projectOptions?.find(
      (p: { value: string; label: string }) => p.value === projectId
    );
    return match?.label ?? projectId;
  }, [projectId, projectOptions]);

  const selectedEmployeeLabel = useMemo(() => {
    if (!employeeId) {
      return "All employees";
    }
    const match = employeeOptions?.find(
      (e: { value: string; label: string }) => e.value === employeeId
    );
    return match?.label ?? employeeId;
  }, [employeeId, employeeOptions]);

  const selectedTaskLabel = useMemo(() => {
    if (!taskId) {
      return "All tasks";
    }
    const match = taskOptions?.find(
      (t: { value: string; label: string }) => t.value === taskId
    );
    return match?.label ?? taskId;
  }, [taskId, taskOptions]);

  const selectedSubTaskLabel = useMemo(() => {
    if (!subTaskId) {
      return "All sub tasks";
    }
    const match = subTaskOptions?.find(
      (st: { value: string; label: string }) => st.value === subTaskId
    );
    return match?.label ?? subTaskId;
  }, [subTaskId, subTaskOptions]);

  /** Card values from API only — no frontend calculations. */
  const cardTotals = useMemo(() => {
    const res = billingResponse;
    return {
      oldHours: res?.old_hours ?? "00:00",
      billableHrs: res?.billable_hrs ?? "00:00",
      totalBillableHrs: res?.total_billable_hrs ?? "0",
      oldBillableValue: parseAmount(res?.old_billable_value),
      newBillableValue: parseAmount(res?.new_billable_value),
      totalBillableValue: parseAmount(res?.total_billable_value),
      totalAgreementCosting: parseAmount(res?.total_agreement_costing),
      actualSalaryCost: parseAmount(res?.actual_salary_cost),
      oldCost: parseAmount(res?.total_old_cost),
      travelling: parseAmount(res?.["total Travelling Expense"]),
      server: parseAmount(res?.["total Server Expense"]),
      hosting: parseAmount(res?.["total Hosting Expense"]),
      totalExpense: parseAmount(res?.["total Expense"]),
      totalCost: parseAmount(res?.total_cost),
      profitLossBillablePct: parseAmount(
        res?.Profit_loss_base_On_billable_value
      ),
      profitLossBudgetPct: parseAmount(
        res?.Profit_loss_base_On_proposal_budget
      ),
      projectHealthPct:
        res?.["Project health"] === undefined ||
        res?.["Project health"] === null
          ? undefined
          : parseAmount(res["Project health"]),
    };
  }, [billingResponse]);

  function marginPercentUi(pct: number, options?: { invertHealth?: boolean }) {
    const invert = options?.invertHealth ?? false;
    const healthy = invert ? pct <= 100 : pct >= 0;
    const barFillPct = Math.min(100, Math.max(0, Math.abs(pct)));
    return {
      formattedValue: `${pct.toFixed(1)}%`,
      badgeLabel: healthy ? "Healthy" : "At Risk",
      badgeClass: healthy
        ? "bg-emerald-600 text-white"
        : "bg-red-600 text-white",
      barClass: healthy ? "bg-emerald-600" : "bg-red-600",
      valueClass: healthy ? "text-emerald-600" : "text-red-600",
      barFillPct,
    };
  }

  const profitLossBillableUi = useMemo(
    () => marginPercentUi(cardTotals.profitLossBillablePct),
    [cardTotals.profitLossBillablePct]
  );

  const profitLossBudgetUi = useMemo(
    () => marginPercentUi(cardTotals.profitLossBudgetPct),
    [cardTotals.profitLossBudgetPct]
  );

  const projectHealthUi = useMemo(() => {
    const pct = cardTotals.projectHealthPct;
    if (pct === undefined || pct === null) {
      return {
        pctDisplay: "—",
        badgeLabel: "N/A",
        badgeClass: "bg-gray-500 text-white",
        barClass: "bg-gray-400",
        valueClass: "text-gray-600",
        barFillPct: 0,
      };
    }
    const ui = marginPercentUi(pct, { invertHealth: true });
    return { ...ui, pctDisplay: ui.formattedValue };
  }, [cardTotals.projectHealthPct]);

  const handleExportExcel = async () => {
    const rows: Array<{
      project: string;
      employee: string;
      role: string;
      billableHoursMinutes: number;
      rate: number;
      actualCost: number;
      billableValue: number;
      expenses: number;
      margin: string;
      currency: string;
    }> = [];
    filteredData.forEach(project => {
      project.employees.forEach(emp => {
        rows.push({
          project: project.project_name,
          employee: emp.employee_name,
          role: String(emp.role ?? ""),
          billableHoursMinutes: parseBillableHoursToMinutes(emp.billable_hrs),
          rate: emp.role_hourly_rate ?? 0,
          actualCost: emp.Actual_cost ?? 0,
          billableValue: emp.billable_value ?? 0,
          expenses: emp.Expenses ?? 0,
          margin: String(emp.Margin ?? "0%"),
          currency: getCurrencyExportValue(project),
        });
      });
    });

    const filterRows: [string, string][] = [
      ["Report Filters", ""],
      ["Start Date", formatDate(startDate, "-")],
      ["End Date", formatDate(endDate, "-")],
      ["Project", selectedProjectLabel],
      ["Employee", selectedEmployeeLabel],
      ["Task", selectedTaskLabel],
      ["Sub task", selectedSubTaskLabel],
      ["Search", searchQuery.trim() || "All"],
    ];

    const filename = `billing-summary-${startDate}-to-${endDate}`;
    await downloadBillingSummaryExcel({
      filterRows,
      dataRows: rows,
      baseFilename: filename,
    });
  };

  const handleReset = () => {
    setSearchQuery("");
    setStartDate(defaultDates.startDate);
    setEndDate(defaultDates.endDate);
    setProjectId(undefined);
    setEmployeeId(undefined);
    setTaskId(undefined);
    setSubTaskId(undefined);
  };

  const formatDate = (date: string, separator: string) => {
    const [year, month, day] = date.split("-");
    return `${day}${separator}${month}${separator}${year}`;
  };

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-4 md:p-0">
      {/* Header */}
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-primary text-xl font-bold md:text-2xl">
            Billing Summary
          </h1>
          <p className="text-sm text-gray-500">
            Billing readiness and transparency view
          </p>
        </div>
        <Button
          onClick={() => void handleExportExcel()}
          className="bg-primary hover:bg-primary/90 flex items-center gap-2 text-white"
        >
          <Download className="h-4 w-4" />
          Export Excel
        </Button>
      </div>

      {/* Summary Metrics Cards */}
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-4 lg:grid-cols-3 xl:grid-cols-5">
        {/* 1. Billing Hours */}
        <Card className="relative overflow-hidden">
          <div className="absolute right-0 top-0 h-24 w-24 rounded-bl-full from-blue-200 to-transparent" />
          <CardContent className="px-5 py-3">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-muted-foreground text-sm font-medium">
                  Billable Hours
                </p>
                <p className="text-foreground mb-2 mt-1 text-xl font-bold md:text-[1.6rem]">
                  {cardTotals.totalBillableHrs}
                </p>
                <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
                  Old hours <b>{cardTotals.oldHours}</b> · Billable hrs{" "}
                  <b>{cardTotals.billableHrs}</b>
                </p>
              </div>
              <div className="rounded-xl bg-blue-500/10 p-3">
                <Clock className="h-5 w-5 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 2. Billable Value */}
        <Card className="relative overflow-hidden">
          <div className="absolute right-0 top-0 h-24 w-24 rounded-bl-full from-green-200 to-transparent" />
          <CardContent className="px-5 py-3">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-muted-foreground text-sm font-medium">
                  Billable Value
                </p>
                <p className="text-success mb-2 mt-1 text-xl font-bold md:text-[1.6rem]">
                  {formatNumber(cardTotals.totalBillableValue)}
                </p>
                <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
                  Old billable{" "}
                  <b>{formatNumber(cardTotals.oldBillableValue)}</b> · New
                  billable <b>{formatNumber(cardTotals.newBillableValue)}</b>
                </p>
              </div>
              <div className="rounded-xl bg-green-500/10 p-3">
                <DollarSign className="h-5 w-5 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 3. Proposal / Budget */}
        <Card className="relative overflow-hidden">
          <div className="absolute right-0 top-0 h-24 w-24 rounded-bl-full from-cyan-200 to-transparent" />
          <CardContent className="px-5 py-3">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-muted-foreground text-sm font-medium">
                  Proposal / Budget
                </p>
                <p className="text-foreground mb-2 mt-1 text-xl font-bold md:text-[1.6rem]">
                  {formatNumber(cardTotals.totalAgreementCosting)}
                </p>
                <p className="text-muted-foreground mt-1 text-xs">
                  From billing summary
                </p>
              </div>
              <div className="rounded-xl bg-cyan-500/10 p-3">
                <Handshake className="h-5 w-5 text-cyan-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 4. Actual Salary Cost */}
        <Card className="relative overflow-hidden">
          <div className="absolute right-0 top-0 h-24 w-24 rounded-bl-full from-yellow-200 to-transparent" />
          <CardContent className="px-5 py-3">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-muted-foreground text-sm font-medium">
                  Actual Salary Cost
                </p>
                <p className="text-foreground mb-2 mt-1 text-xl font-bold md:text-[1.6rem]">
                  {formatNumber(cardTotals.actualSalaryCost)}
                </p>
                <p className="text-muted-foreground mt-1 text-xs">
                  Direct labor costs
                </p>
              </div>
              <div className="rounded-xl bg-yellow-500/10 p-3">
                <DollarSign className="h-5 w-5 text-yellow-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 7. Total Cost */}
        <Card className="relative overflow-hidden">
          <div className="absolute right-0 top-0 h-24 w-24 rounded-bl-full from-amber-200 to-transparent" />
          <CardContent className="px-5 py-3">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-muted-foreground text-sm font-medium">
                  Total Cost
                </p>
                <p className="text-foreground mb-2 mt-1 text-xl font-bold tabular-nums md:text-[1.6rem]">
                  {formatNumber(cardTotals.totalCost)}
                </p>
                <p className="text-muted-foreground mt-1 text-xs">
                  Actual salary{" "}
                  <b>{formatNumber(cardTotals.actualSalaryCost)}</b> + Old cost{" "}
                  <b>{formatNumber(cardTotals.oldCost)}</b> + Total expense{" "}
                  <b>{formatNumber(cardTotals.totalExpense)}</b>
                </p>
              </div>
              <div className="rounded-xl bg-amber-500/10 p-3">
                <DollarSign className="h-5 w-5 text-amber-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 6. Total Expense */}
        <Card className="relative overflow-hidden">
          <div className="absolute right-0 top-0 h-24 w-24 rounded-bl-full from-orange-200 to-transparent" />
          <CardContent className="px-5 py-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-muted-foreground text-sm font-medium">
                  Total Expense
                </p>
                <p className="text-foreground mb-2 mt-1 text-xl font-bold tabular-nums md:text-[1.6rem]">
                  {formatNumber(cardTotals.totalExpense)}
                </p>
                <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
                  Travelling <b>{formatNumber(cardTotals.travelling)}</b> ·
                  Server <b>{formatNumber(cardTotals.server)}</b> · Hosting{" "}
                  <b>{formatNumber(cardTotals.hosting)}</b>
                </p>
              </div>
              <div className="rounded-xl bg-orange-500/10 p-3">
                <Wallet className="h-5 w-5 text-orange-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 5. Old Cost */}
        <Card className="relative overflow-hidden">
          <div className="absolute right-0 top-0 h-24 w-24 rounded-bl-full from-slate-200 to-transparent" />
          <CardContent className="px-5 py-3">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-muted-foreground text-sm font-medium">
                  Old Cost
                </p>
                <p className="text-foreground mb-2 mt-1 text-xl font-bold tabular-nums md:text-[1.6rem]">
                  {formatNumber(cardTotals.oldCost)}
                </p>
                <p className="text-muted-foreground mt-1 text-xs">
                  Legacy / prior cost
                </p>
              </div>
              <div className="rounded-xl bg-slate-500/10 p-3">
                <Archive className="h-5 w-5 text-slate-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 8. Profit / loss (Billable Value) */}
        <Card className="relative overflow-hidden">
          <div className="absolute right-0 top-0 h-24 w-24 rounded-bl-full from-purple-200 to-transparent" />
          <CardContent className="px-5 py-3">
            <div className="flex flex-col gap-0">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-muted-foreground text-sm font-medium">
                    Profit / loss (Billable Value)
                  </p>
                  <p
                    className={`mb-2 mt-1 text-xl font-bold tabular-nums md:text-[1.6rem] ${profitLossBillableUi.valueClass}`}
                  >
                    {profitLossBillableUi.formattedValue}
                  </p>
                  {/* <p className="text-muted-foreground mt-1 text-xs">
                    (Billable Value − Total Cost) ÷ Billable Value × 100
                  </p> */}
                </div>
                <div className="rounded-xl bg-purple-500/10 p-3">
                  <Percent className="h-5 w-5 text-purple-700" />
                </div>
              </div>
              <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-gray-200">
                <div
                  className={`h-full rounded-full transition-all ${profitLossBillableUi.barClass}`}
                  style={{ width: `${profitLossBillableUi.barFillPct}%` }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 9. Profit / loss (Proposal / Budget) */}
        <Card className="relative overflow-hidden">
          <div className="absolute right-0 top-0 h-24 w-24 rounded-bl-full from-violet-200 to-transparent" />
          <CardContent className="px-5 py-3">
            <div className="flex flex-col gap-0">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-muted-foreground text-sm font-medium">
                    Profit / loss (Proposal / Budget)
                  </p>
                  <p
                    className={`mb-2 mt-1 text-xl font-bold tabular-nums md:text-[1.6rem] ${profitLossBudgetUi.valueClass}`}
                  >
                    {profitLossBudgetUi.formattedValue}
                  </p>
                  {/* <p className="text-muted-foreground mt-1 text-xs">
                    (Proposal / Budget − Total Cost) ÷ Proposal / Budget × 100
                  </p> */}
                </div>
                <div className="rounded-xl bg-violet-500/10 p-3">
                  <Percent className="h-5 w-5 text-violet-700" />
                </div>
              </div>
              <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-gray-200">
                <div
                  className={`h-full rounded-full transition-all ${profitLossBudgetUi.barClass}`}
                  style={{ width: `${profitLossBudgetUi.barFillPct}%` }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 10. Project health */}
        <Card className="relative overflow-hidden">
          <div className="absolute right-0 top-0 h-24 w-24 rounded-bl-full from-rose-200 to-transparent" />
          <CardContent className="px-5 py-3">
            <div className="flex flex-col gap-0">
              <div className="flex items-start justify-between gap-2">
                <p className="text-muted-foreground text-sm font-medium">
                  Project Health
                </p>
                <span
                  className={`z-10 shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${projectHealthUi.badgeClass}`}
                >
                  {projectHealthUi.badgeLabel}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <p
                  className={`m+d:text-[1.6rem] mb-2 text-xl font-bold tabular-nums ${projectHealthUi.valueClass}`}
                >
                  {projectHealthUi.pctDisplay}
                </p>
                {/* <p className="text-muted-foreground mt-1 text-xs">
                  Total Cost ÷ Proposal / Budget × 100
                </p> */}
                <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-gray-200">
                  <div
                    className={`h-full rounded-full transition-all ${projectHealthUi.barClass}`}
                    style={{ width: `${projectHealthUi.barFillPct}%` }}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      {/* <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="border border-gray-200 bg-white shadow-sm">
          <CardContent className="p-5">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Total Billable Hours
            </p>
            <p className="text-primary mt-1 text-2xl font-bold">
              {totals.totalBillableHrs}h
            </p>
          </CardContent>
        </Card>
        <Card className="border border-gray-200 bg-white shadow-sm">
          <CardContent className="p-5">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Total Billable Value
            </p>
            <p className="mt-1 text-2xl font-bold text-green-600">
              ${totals.totalBillableValue.toLocaleString()}
            </p>
          </CardContent>
        </Card>
        <Card className="border border-gray-200 bg-white shadow-sm">
          <CardContent className="p-5">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Total Cost
            </p>
            <p className="text-primary mt-1 text-2xl font-bold">
              ${totals.totalCost.toLocaleString()}
            </p>
          </CardContent>
        </Card>
        <Card className="border border-gray-200 bg-white shadow-sm">
          <CardContent className="p-5">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Average Margin
            </p>
            <p className="mt-1 text-2xl font-bold text-green-600">
              {totals.averageMargin}%
            </p>
          </CardContent>
        </Card>
      </div> */}

      {/* Filter and Action Bar */}
      <Card className="border border-gray-200 bg-white">
        <CardContent className="p-3 md:p-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-3 md:gap-4">
            {/* <div className="relative flex-1">
              <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
              <Input
                placeholder="Search employees, projects..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div> */}
            <div className="flex flex-wrap items-end gap-2">
              <div className="space-y-2">
                <Label htmlFor="startDate" className="text-sm text-gray-700">
                  Start / Live Date
                </Label>
                <div className="relative">
                  <input
                    type="date"
                    id="startDate"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value || "")}
                    className="w-full rounded-lg border bg-[#fafafa] px-3 py-1 pr-8 text-sm"
                  />
                  {startDate && startDate !== defaultDates.startDate && (
                    <button
                      onClick={() => setStartDate(defaultDates.startDate)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      title="Reset to default"
                      type="button"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="endDate" className="text-sm text-gray-700">
                  End Date
                </Label>
                <div className="relative">
                  <input
                    type="date"
                    id="endDate"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value || "")}
                    className="w-full rounded-lg border bg-[#fafafa] px-3 py-1 pr-8 text-sm"
                  />
                  {endDate && endDate !== defaultDates.endDate && (
                    <button
                      onClick={() => setEndDate(defaultDates.endDate)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      title="Reset to default"
                      type="button"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-gray-700">Project</Label>
                <div className="relative">
                  <Select
                    allowClear
                    placeholder="All projects"
                    value={projectId ?? ""}
                    onChange={val => handleProjectChange(val || undefined)}
                    className="w-[180px]"
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={[
                      { value: "", label: "All projects" },
                      ...(projectOptions?.map(
                        (p: { value: string; label: string }) => ({
                          value: p.value,
                          label: p.label,
                        })
                      ) ?? []),
                    ]}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-gray-700">Task</Label>
                <div className="relative">
                  <Select
                    allowClear
                    placeholder={
                      projectId ? "All tasks" : "Select a project first"
                    }
                    value={taskId ?? ""}
                    onChange={val => handleTaskChange(val || undefined)}
                    className="w-[180px]"
                    disabled={!projectId}
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={[
                      { value: "", label: "All tasks" },
                      ...(taskOptions?.map(
                        (t: { value: string; label: string }) => ({
                          value: t.value,
                          label: t.label,
                        })
                      ) ?? []),
                    ]}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-gray-700">Sub task</Label>
                <div className="relative">
                  <Select
                    allowClear
                    placeholder={
                      taskId ? "All sub tasks" : "Select a task first"
                    }
                    value={subTaskId ?? ""}
                    onChange={val => setSubTaskId(val || undefined)}
                    className="w-[180px]"
                    disabled={!taskId}
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={[
                      { value: "", label: "All sub tasks" },
                      ...(subTaskOptions?.map(
                        (st: { value: string; label: string }) => ({
                          value: st.value,
                          label: st.label,
                        })
                      ) ?? []),
                    ]}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-gray-700">Employee</Label>
                <div className="relative">
                  <Select
                    allowClear
                    placeholder="All employees"
                    value={employeeId ?? ""}
                    onChange={val => setEmployeeId(val || undefined)}
                    className="w-[180px]"
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={[
                      { value: "", label: "All employees" },
                      ...(employeeOptions?.map(
                        (e: { value: string; label: string }) => ({
                          value: e.value,
                          label: e.label,
                        })
                      ) ?? []),
                    ]}
                  />
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={handleReset}>
                <RotateCcw className="mr-1 h-4 w-4" />
                Reset
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Billing Details - grouped by project from API */}
      <Card className="border border-gray-200 bg-white shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <FileText className="text-primary h-5 w-5" />
            <CardTitle className="text-primary text-base font-semibold">
              Billing Details
            </CardTitle>
          </div>
          <p className="text-sm text-gray-500">
            Grouped by project · {formatDate(startDate, "-")} to{" "}
            {formatDate(endDate, "-")}
          </p>
        </CardHeader>
        <CardContent className="p-0">
          {billingLoading ? (
            <div className="py-12 text-center text-gray-500">
              Loading billing summary...
            </div>
          ) : filteredData.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              No billing data found for the selected filters
            </div>
          ) : (
            <Collapse
              defaultActiveKey={
                filteredData.length > 0 ? [filteredData[0].project_id] : []
              }
              items={filteredData.map(project => ({
                key: project.project_id,
                label: (
                  <div className="flex w-full items-center justify-between pr-4">
                    <span className="text-primary font-medium">
                      {project.project_name} ({project.employees.length}{" "}
                      employee{project.employees.length !== 1 ? "s" : ""})
                    </span>
                    <span className="text-muted-foreground text-sm">
                      {project.currency_symbol}
                    </span>
                  </div>
                ),
                children: (
                  <div className="w-full overflow-x-auto py-2">
                    <Table className="w-full min-w-[800px] table-fixed">
                      <colgroup>
                        <col className="w-[220px]" />
                        <col className="w-[200px]" />
                      </colgroup>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="font-semibold text-gray-700">
                            Employee
                          </TableHead>
                          <TableHead className="border-border w-[200px] max-w-[200px] border-r px-3 align-middle font-semibold text-gray-700">
                            Role
                          </TableHead>
                          <TableHead className="pl-4 text-center font-semibold text-gray-700">
                            Billable Hrs
                          </TableHead>
                          <TableHead className="text-center font-semibold text-gray-700">
                            Rate
                          </TableHead>
                          <TableHead className="text-center font-semibold text-gray-700">
                            Actual Cost
                          </TableHead>
                          <TableHead className="text-center font-semibold text-gray-700">
                            Billable Value
                          </TableHead>
                          <TableHead className="text-center font-semibold text-gray-700">
                            Expenses
                          </TableHead>
                          {/* <TableHead className="text-center font-semibold text-gray-700">
                            Profit/Loss
                          </TableHead> */}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {project.employees.map(emp => (
                          <TableRow
                            key={`${project.project_id}-${emp.employee_id}`}
                            className="hover:bg-gray-50"
                          >
                            <TableCell className="max-w-[220px] truncate text-gray-700">
                              {emp.employee_name}
                            </TableCell>
                            <TableCell className="border-border w-[200px] max-w-[200px] truncate border-r px-3 align-middle text-gray-700">
                              {emp.role ?? "—"}
                            </TableCell>
                            <TableCell className="text-primary pl-4 text-center font-semibold">
                              {emp.billable_hrs ?? "--"}
                            </TableCell>
                            <TableCell className="text-center text-gray-600">
                              {formatCurrencyValue(
                                project.currency_symbol ?? "",
                                emp.role_hourly_rate
                              )}
                            </TableCell>
                            <TableCell className="text-center text-gray-600">
                              {formatCurrencyValue(
                                project.currency_symbol ?? "",
                                emp.Actual_cost
                              )}
                            </TableCell>
                            <TableCell className="text-center font-semibold text-green-600">
                              {formatCurrencyValue(
                                project.currency_symbol ?? "",
                                emp.billable_value
                              )}
                            </TableCell>
                            <TableCell className="text-center text-gray-600">
                              {formatCurrencyValue(
                                project.currency_symbol ?? "",
                                emp.Expenses
                              )}
                            </TableCell>
                            {/* <TableCell className="text-center font-medium text-blue-600">
                              {emp.Margin ?? "--"}
                            </TableCell> */}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ),
              }))}
              className="border-0 bg-transparent"
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BillingSummaryPage;
